import React, { useState, useEffect } from 'react'
import { MapPin, Save, X } from 'lucide-react'
import { Button, Input } from '../ui'
import {
  Address,
  CreateAddressRequest,
  UpdateAddressRequest
} from '../../services/organizationService'

interface AddressFormProps {
  address?: Address | null
  organizationId: string
  onSave: (data: CreateAddressRequest | UpdateAddressRequest) => Promise<void>
  onCancel: () => void
  isSaving: boolean
}

// ANSI 2-character country codes
const COUNTRY_CODES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AT', name: 'Austria' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'IE', name: 'Ireland' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'GR', name: 'Greece' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'HU', name: 'Hungary' },
  { code: 'RO', name: 'Romania' },
  { code: 'JP', name: 'Japan' },
  { code: 'CN', name: 'China' },
  { code: 'IN', name: 'India' },
  { code: 'KR', name: 'South Korea' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'CO', name: 'Colombia' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'SG', name: 'Singapore' },
  { code: 'MY', name: 'Malaysia' },
  { code: 'TH', name: 'Thailand' },
  { code: 'ID', name: 'Indonesia' },
  { code: 'PH', name: 'Philippines' },
  { code: 'VN', name: 'Vietnam' }
]

const AddressForm: React.FC<AddressFormProps> = ({
  address,
  onSave,
  onCancel,
  isSaving
}) => {
  const [formData, setFormData] = useState({
    addressLine1: address?.addressLine1 || '',
    addressLine2: address?.addressLine2 || '',
    locality: address?.locality || '',
    region: address?.region || '',
    postalCode: address?.postalCode || '',
    countryCode: address?.countryCode || 'US'
  })

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  // Track original data for change detection
  const [originalData, setOriginalData] = useState(formData)

  useEffect(() => {
    const initial = {
      addressLine1: address?.addressLine1 || '',
      addressLine2: address?.addressLine2 || '',
      locality: address?.locality || '',
      region: address?.region || '',
      postalCode: address?.postalCode || '',
      countryCode: address?.countryCode || 'US'
    }
    setFormData(initial)
    setOriginalData(initial)
  }, [address])

  const hasChanges = () => {
    return (
      formData.addressLine1 !== originalData.addressLine1 ||
      formData.addressLine2 !== originalData.addressLine2 ||
      formData.locality !== originalData.locality ||
      formData.region !== originalData.region ||
      formData.postalCode !== originalData.postalCode ||
      formData.countryCode !== originalData.countryCode
    )
  }

  const isValid = () => {
    return (
      formData.addressLine1.trim().length > 0 &&
      formData.locality.trim().length > 0 &&
      formData.region.trim().length > 0 &&
      formData.postalCode.trim().length > 0 &&
      formData.countryCode.trim().length > 0
    )
  }

  const canSubmit = () => {
    if (address) {
      // Editing: must have changes and be valid
      return hasChanges() && isValid() && !isSaving
    } else {
      // Creating: just must be valid
      return isValid() && !isSaving
    }
  }

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formData.addressLine1.trim()) {
      errors.addressLine1 = 'Address line 1 is required'
    }

    if (!formData.locality.trim()) {
      errors.locality = 'City is required'
    }

    if (!formData.region.trim()) {
      errors.region = 'State/Region is required'
    }

    if (!formData.postalCode.trim()) {
      errors.postalCode = 'Postal code is required'
    }

    if (!formData.countryCode.trim()) {
      errors.countryCode = 'Country is required'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    let data: CreateAddressRequest | UpdateAddressRequest

    if (!address) {
      // Creating new address
      data = {
        addressLine1: formData.addressLine1.trim(),
        addressLine2: formData.addressLine2.trim() || null,
        addressLine3: null,
        locality: formData.locality.trim(),
        region: formData.region.trim(),
        postalCode: formData.postalCode.trim(),
        countryCode: formData.countryCode.trim(),
        notes: null,
        addressTypeId: 1,
        isPrimary: true
      }
    } else {
      // Updating existing address
      data = {
        addressLine1: formData.addressLine1.trim(),
        addressLine2: formData.addressLine2.trim() || null,
        addressLine3: null,
        locality: formData.locality.trim(),
        region: formData.region.trim(),
        postalCode: formData.postalCode.trim(),
        countryCode: formData.countryCode.trim(),
        notes: null,
        isPrimary: true
      }
    }

    await onSave(data)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Address Line 1 */}
      <Input
        label="Address Line 1 *"
        value={formData.addressLine1}
        onChange={(e) => handleChange('addressLine1', e.target.value)}
        placeholder="Street address, P.O. box"
        icon={<MapPin className="h-3.5 w-3.5" />}
        error={validationErrors.addressLine1}
        disabled={isSaving}
      />

      {/* Address Line 2 */}
      <Input
        label="Address Line 2"
        value={formData.addressLine2}
        onChange={(e) => handleChange('addressLine2', e.target.value)}
        placeholder="Apartment, suite, unit, building, floor, etc."
        disabled={isSaving}
      />

      {/* City and State */}
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="City *"
          value={formData.locality}
          onChange={(e) => handleChange('locality', e.target.value)}
          placeholder="City"
          error={validationErrors.locality}
          disabled={isSaving}
        />

        <Input
          label="State/Region *"
          value={formData.region}
          onChange={(e) => handleChange('region', e.target.value)}
          placeholder="State or Region"
          error={validationErrors.region}
          disabled={isSaving}
        />
      </div>

      {/* Postal Code and Country */}
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Postal Code *"
          value={formData.postalCode}
          onChange={(e) => handleChange('postalCode', e.target.value)}
          placeholder="Postal/ZIP code"
          error={validationErrors.postalCode}
          disabled={isSaving}
        />

        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Country *
          </label>
          <select
            value={formData.countryCode}
            onChange={(e) => handleChange('countryCode', e.target.value)}
            disabled={isSaving}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-avi-primary/50 focus:border-avi-primary transition-colors duration-200"
          >
            {COUNTRY_CODES.map((country) => (
              <option key={country.code} value={country.code}>
                {country.code} - {country.name}
              </option>
            ))}
          </select>
          {validationErrors.countryCode && (
            <p className="mt-1 text-xs text-avi-danger">{validationErrors.countryCode}</p>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button variant="outline" size='xs' onClick={onCancel} disabled={isSaving}>
          <X className="h-3.5 w-3.5 mr-2" />
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size='xs'
          disabled={!canSubmit()}
          loading={isSaving}
        >
          <Save className="h-3.5 w-3.5 mr-2" />
          {address ? 'Update Address' : 'Add Address'}
        </Button>
      </div>
    </form>
  )
}

export default AddressForm
